import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreateNoteModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    related_to_type: 'client',
    related_to_id: '',
    tags: ''
  });
  const [loading, setLoading] = useState(false);
  const [entities, setEntities] = useState([]);
  const { toast } = useToast();

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (isOpen && formData.related_to_type) {
      fetchEntities(formData.related_to_type);
    }
  }, [isOpen, formData.related_to_type]);

  const fetchEntities = async (entityType) => {
    try {
      let endpoint = '';
      switch (entityType) {
        case 'client':
          endpoint = '/api/crm/clients';
          break;
        case 'lead':
          endpoint = '/api/crm/leads';
          break;
        case 'opportunity':
          endpoint = '/api/crm/opportunities';
          break;
        case 'contact':
          endpoint = '/api/crm/contacts';
          break;
        default:
          return;
      }

      const response = await fetch(`${API_URL}${endpoint}?limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch entities');

      const data = await response.json();
      setEntities(data);
    } catch (error) {
      console.error('Error fetching entities:', error);
      setEntities([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.subject.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Subject is required',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.related_to_id) {
      toast({
        title: 'Validation Error',
        description: 'Please select a related entity',
        variant: 'destructive'
      });
      return;
    }

    try {
      setLoading(true);

      const payload = {
        type: 'note',
        subject: formData.subject.trim(),
        description: formData.description.trim(),
        related_to_type: formData.related_to_type,
        related_to_id: formData.related_to_id,
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(Boolean) : [],
        priority: 'medium'
      };

      const response = await fetch(`${API_URL}/api/crm/notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Failed to create note');

      toast({
        title: 'Success',
        description: 'Note created successfully'
      });

      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Error creating note:', error);
      toast({
        title: 'Error',
        description: 'Failed to create note',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      subject: '',
      description: '',
      related_to_type: 'client',
      related_to_id: '',
      tags: ''
    });
    onClose();
  };

  const getEntityLabel = (entity) => {
    if (formData.related_to_type === 'client') {
      return entity.company_name || 'Unknown';
    } else if (formData.related_to_type === 'lead') {
      return entity.company_name || 'Unknown';
    } else if (formData.related_to_type === 'opportunity') {
      return entity.name || 'Unknown';
    } else if (formData.related_to_type === 'contact') {
      return entity.name || 'Unknown';
    }
    return 'Unknown';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-[#2B2D42]">Create New Note</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
                placeholder="Enter note subject"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={6}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent resize-none"
                placeholder="Enter detailed notes..."
              />
            </div>

            {/* Related To Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Related To <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.related_to_type}
                onChange={(e) => {
                  setFormData({ ...formData, related_to_type: e.target.value, related_to_id: '' });
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
                required
              >
                <option value="client">Client</option>
                <option value="lead">Lead</option>
                <option value="opportunity">Opportunity</option>
                <option value="contact">Contact</option>
              </select>
            </div>

            {/* Entity Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select {formData.related_to_type.charAt(0).toUpperCase() + formData.related_to_type.slice(1)} <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.related_to_id}
                onChange={(e) => setFormData({ ...formData, related_to_id: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
                required
              >
                <option value="">Select {formData.related_to_type}...</option>
                {entities.map((entity) => (
                  <option key={entity.id} value={entity.id}>
                    {getEntityLabel(entity)}
                  </option>
                ))}
              </select>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tags
              </label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
                placeholder="e.g., important, follow-up, meeting (comma-separated)"
              />
              <p className="text-xs text-gray-500 mt-1">Separate tags with commas</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
            <Button
              type="button"
              onClick={handleClose}
              variant="outline"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#00B4D8] hover:bg-[#0096B8]"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Note'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateNoteModal;
